/**
 * Sandbox resource (CT-5718): a record's own Sandbox — claims grouped by source,
 * waiting to be confirmed, and the conflicts between them. A mirror of
 * Governance Studio's sandbox scoped to one record, with no governance filters.
 *
 * The paths are ASSUMED, `TODO(CT-5718 / backend)`. Real signatures, snake_case
 * bodies; only the transport is stubbed. The chips, filters and search narrow on
 * the server.
 */

import { validateApiResponse } from '../shared/utils/apiResponse.js';
import { request } from './mockTransport.js';

export { __resetMockSandboxForTests } from './__mocks__/sandbox.mock.js';

const mapClaim = (raw = {}) => ({
  id: raw.id ?? raw.claim_id ?? '',
  label: raw.label ?? '',
  value: raw.value ?? '',
  category: raw.category ?? null,
  quote: raw.quote ?? null,
  speaker: raw.speaker ?? null,
  file: raw.file ?? null,
  timestamp: raw.timestamp ?? null,
  status: raw.status ?? 'to_confirm',
  canConfirm: Boolean(raw.can_confirm ?? raw.canConfirm ?? false),
  sent: Boolean(raw.sent ?? false),
});

/** One source group and its claims. `state` is `conflict` while a claim in it disagrees. */
export const mapApiSandboxGroupToUi = (raw = {}) => ({
  id: raw.id ?? '',
  label: raw.label ?? '',
  occurredAt: raw.occurred_at ?? raw.occurredAt ?? null,
  periodLabel: raw.period_label ?? raw.periodLabel ?? null,
  claimCount: raw.claim_count ?? raw.claimCount ?? raw.claims?.length ?? 0,
  sourceType: raw.source_type ?? raw.sourceType ?? null,
  channel: raw.channel ?? null,
  state: raw.state ?? null,
  sourceRef: raw.source_ref
    ? { kind: raw.source_ref.kind ?? null, id: raw.source_ref.id ?? null, folderId: raw.source_ref.folder_id ?? null }
    : null,
  claims: (raw.claims ?? []).map(mapClaim),
});

export const mapApiConflictToUi = (raw = {}) => ({
  claimId: raw.claim_id ?? raw.claimId ?? '',
  label: raw.label ?? '',
  resolved: Boolean(raw.resolved),
  canConfirm: Boolean(raw.can_confirm ?? raw.canConfirm ?? false),
  statements: (raw.statements ?? []).map((statement) => ({
    id: statement.id ?? '',
    person: statement.person ?? '',
    sourceType: statement.source_type ?? statement.sourceType ?? null,
    source: statement.source ?? null,
    quote: statement.quote ?? null,
    value: statement.value ?? null,
  })),
});

const record = (entityType, entityId) => ({ entity_type: entityType, entity_id: entityId });

/**
 * TODO(CT-5718 / backend): `POST /v0/profile/sandbox/groups/list` with `{ entity_type,
 * entity_id, order_by, order_direction, quick_filter, filters: { source, source_type },
 * search: { query, fields } }`. `order_by`: `occurred_at` (Most recent), `claim_count`
 * (Most claims) or `conflicts` (Conflicts first).
 */
export const listSandboxGroups = async ({
  entityType,
  entityId,
  orderBy = 'occurred_at',
  orderDirection = 'desc',
  quickFilter = 'all',
  filters,
  search,
} = {}) => {
  const response = await request('/v0/profile/sandbox/groups/list', {
    ...record(entityType, entityId),
    order_by: orderBy,
    order_direction: orderDirection,
    quick_filter: quickFilter,
    ...(filters ? { filters } : {}),
    ...(search ? { search } : {}),
  });
  return validateApiResponse(response, 'Failed to load the Sandbox');
};

/** TODO(CT-5718 / backend): `POST /v0/profile/sandbox/claims/confirm` with `{ entity_type, entity_id, claim_id }`. */
export const confirmClaim = async ({ entityType, entityId, claimId } = {}) => {
  const response = await request('/v0/profile/sandbox/claims/confirm', { ...record(entityType, entityId), claim_id: claimId });
  return validateApiResponse(response, 'Failed to confirm the claim');
};

/** TODO(CT-5718 / backend): `POST /v0/profile/sandbox/claims/send-confirmation` with `{ entity_type, entity_id, claim_id }`. */
export const sendClaimForConfirmation = async ({ entityType, entityId, claimId } = {}) => {
  const response = await request('/v0/profile/sandbox/claims/send-confirmation', {
    ...record(entityType, entityId),
    claim_id: claimId,
  });
  return validateApiResponse(response, 'Failed to send the claim for confirmation');
};

/** TODO(CT-5718 / backend): `POST /v0/profile/sandbox/conflicts/get` with `{ entity_type, entity_id, claim_id }`. */
export const getConflict = async ({ entityType, entityId, claimId } = {}) => {
  const response = await request('/v0/profile/sandbox/conflicts/get', { ...record(entityType, entityId), claim_id: claimId });
  return validateApiResponse(response, 'Failed to load the conflict');
};

/**
 * TODO(CT-5718 / backend): `POST /v0/profile/sandbox/conflicts/resolve` with
 * `{ entity_type, entity_id, claim_id, choice }` — a statement id, or `unsure`.
 */
export const resolveConflict = async ({ entityType, entityId, claimId, choice } = {}) => {
  const response = await request('/v0/profile/sandbox/conflicts/resolve', {
    ...record(entityType, entityId),
    claim_id: claimId,
    choice,
  });
  return validateApiResponse(response, 'Failed to resolve the conflict');
};
